// The draft editor's rules, kept free of React so they can be tested.
//
// ⚠ WHY THIS FILE EXISTS. Until 2026-09-24 nobody could set a limit in the app.
// "Draft a new version" posted an identical copy at once — the API creates a
// draft whole (POST /specs) and has no edit route — and the spec master's "New
// draft" form took one parameter and never sent IntervalMinutes, so an in-process
// check drafted there was asked once per run and never again. The pilot gives the
// person who owns a product the job of setting its limits; these are the rules
// that let the editor do it without losing anything the server needs.
//
// The server remains the gate. Its CHECK constraints refuse a crossed band or a
// banded yes/no question whatever this file says; the checks here exist so the
// person typing is told which row and which field, before they press Save.

export type CheckKind = 'FIRST_OFF' | 'ADHERENCE' | 'BOTH' | 'READINESS' | 'COFA' | 'RECEIPT';
export type Aggregation = 'ALL' | 'MEAN' | 'AQS';
export type ValueKind = 'NUMERIC' | 'ATTRIBUTE';
export type ControlPointType = 'NONE' | 'CCP' | 'OPRP';

/** A parameter as `GET /spec-versions/{id}` returns it — the two bands nested. */
export interface ApiParameter {
  /** The parameter's own row id. A draft made from it names it (`referencePhotosFrom`) so its
   *  reference photos carry over (migration 034). Absent on a payload that does not send it. */
  id?: number;
  code: string;
  name: string;
  unit: string | null;
  sequence: number;
  checkKind: CheckKind;
  controlPointType: ControlPointType;
  valueKind?: ValueKind;
  section?: string | null;
  requiresEvidenceOnFail?: boolean;
  intervalMinutes: number | null;
  sampleCount: number;
  aggregation: Aggregation;
  rejectLimits: { min: number | null; max: number | null };
  actionLimits: { lower: number | null; upper: number | null };
  nominal: number | null;
  requiresEvidence: boolean;
  requiresInstrument: boolean;
  instrumentKind: string | null;
  /** A raw material's COFA check (migration 014). Absent on older versions. */
  expectedText?: string | null;
  isMandatory?: boolean;
  /** Its live reference photos (migration 034). Null or absent = not sent, never "none" (rule 15). */
  referencePhotos?: Array<{ photoId: number; caption: string | null; sha256: string }> | null;
}

/**
 * One row of the editor. Numbers are held as TEXT: "1." is a valid state to be
 * part-way through typing, and a field that coerces on every keystroke fights the
 * person using it.
 */
export interface DraftRow {
  /** React's key. Not the code: two rows can share a code while being edited. */
  key: string;
  code: string;
  name: string;
  unit: string;
  checkKind: CheckKind;
  intervalMinutes: string;
  valueKind: ValueKind;
  minValue: string;
  actionLower: string;
  nominal: string;
  actionUpper: string;
  maxValue: string;
  sampleCount: string;
  aggregation: Aggregation;
  controlPointType: ControlPointType;
  section: string;
  requiresEvidenceOnFail: boolean;
  requiresEvidence: boolean;
  requiresInstrument: boolean;
  instrumentKind: string;
  /** What a certificate result must read. Carried into every copy - a copy must not drop what the
   *  certificate is held to - and, since UX-29 (31 Oct 2026), shown and edited on COFA and RECEIPT rows. */
  expectedText: string;
  isMandatory: boolean;
  /** The parameter this row was carried over from, or null for a row added here. Sent as
   *  `referencePhotosFrom`, so the server copies that parameter's live reference photos onto the
   *  new row: without it every limit change would mean photographing every check again. */
  sourceParameterId: number | null;
  /** How many reference photos the source parameter had when the editor opened; null = not known. */
  referencePhotoCount: number | null;
}

let counter = 0;
function nextKey(): string {
  counter += 1;
  return `row-${counter}`;
}

function text(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

/** An existing parameter, ready to edit. Every field the server stores comes
 *  across — dropping IntervalMinutes is exactly the defect this file replaces. */
export function rowFromApi(p: ApiParameter): DraftRow {
  return {
    key: nextKey(),
    code: p.code,
    name: p.name,
    unit: p.unit ?? '',
    checkKind: p.checkKind,
    intervalMinutes: text(p.intervalMinutes),
    valueKind: p.valueKind ?? 'NUMERIC',
    minValue: text(p.rejectLimits.min),
    actionLower: text(p.actionLimits.lower),
    nominal: text(p.nominal),
    actionUpper: text(p.actionLimits.upper),
    maxValue: text(p.rejectLimits.max),
    sampleCount: String(p.sampleCount),
    aggregation: p.aggregation,
    controlPointType: p.controlPointType,
    section: p.section ?? '',
    requiresEvidenceOnFail: p.requiresEvidenceOnFail ?? false,
    requiresEvidence: p.requiresEvidence,
    requiresInstrument: p.requiresInstrument,
    instrumentKind: p.instrumentKind ?? '',
    expectedText: p.expectedText ?? '',
    isMandatory: p.isMandatory ?? true,
    sourceParameterId: typeof p.id === 'number' && Number.isInteger(p.id) && p.id > 0 ? p.id : null,
    referencePhotoCount: Array.isArray(p.referencePhotos) ? p.referencePhotos.length : null,
  };
}

/**
 * A new, empty parameter. A product's defaults to BOTH — checked at first-off and
 * then through the run — because that is what every one of the 65 loaded from the
 * v6.6 lab summary is. A line's defaults to the start-up form's shape: a yes/no
 * READINESS item that needs a comment when the answer is No.
 */
export function blankRow(isProduct: boolean): DraftRow {
  return {
    key: nextKey(),
    code: '',
    name: '',
    unit: '',
    checkKind: isProduct ? 'BOTH' : 'READINESS',
    intervalMinutes: '',
    valueKind: isProduct ? 'NUMERIC' : 'ATTRIBUTE',
    minValue: '',
    actionLower: '',
    nominal: '',
    actionUpper: '',
    maxValue: '',
    sampleCount: '1',
    aggregation: 'ALL',
    controlPointType: 'NONE',
    section: '',
    requiresEvidenceOnFail: !isProduct,
    requiresEvidence: false,
    requiresInstrument: false,
    instrumentKind: '',
    expectedText: '',
    isMandatory: true,
    sourceParameterId: null,
    referencePhotoCount: null,
  };
}

/** Empty is `null`; anything that is not a finite number is `undefined`, so a
 *  typo is reported rather than quietly saved as "no limit". */
export function parseNumber(value: string): number | null | undefined {
  const t = value.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** Does this check repeat through the run? Only then does an interval mean anything. */
export function isInProcess(kind: CheckKind): boolean {
  return kind === 'ADHERENCE' || kind === 'BOTH';
}

/** A raw material's check (migration 014): on the supplier's certificate, or tested at receipt.
 *  Judged against a limit OR the words the result must read (`expectedText`), never on the line
 *  (UI/UX audit, 31 Oct 2026, UX-29). */
export function isCertificateKind(kind: CheckKind): boolean {
  return kind === 'COFA' || kind === 'RECEIPT';
}

/** A certificate row whose result is words and that carries no number at all: the editor shows
 *  its expected text in place of the measurement fields (UX-29). A row with any number set keeps
 *  them, so nothing it carries is ever hidden from the person saving it. */
export function judgedByText(row: DraftRow): boolean {
  return (
    isCertificateKind(row.checkKind) &&
    row.expectedText.trim() !== '' &&
    row.unit.trim() === '' &&
    [row.minValue, row.actionLower, row.nominal, row.actionUpper, row.maxValue].every((v) => v.trim() === '')
  );
}

/** The editable fields a problem can point at: the DraftRow keys the editor renders. */
export type DraftField =
  | 'code'
  | 'name'
  | 'checkKind'
  | 'intervalMinutes'
  | 'valueKind'
  | 'minValue'
  | 'actionLower'
  | 'nominal'
  | 'actionUpper'
  | 'maxValue'
  | 'sampleCount'
  | 'aggregation'
  | 'instrumentKind'
  | 'expectedText';

/**
 * One problem, with where it is. `row` is the index on screen and `field` the
 * DraftRow key, so the editor can mark the input and put the sentence under it
 * (UI/UX audit item 16, 27 Oct 2026); a problem about the version as a whole
 * carries neither. The sentence still names the row, because the summary list
 * reads on its own.
 */
export interface DraftProblem {
  message: string;
  row: number | null;
  field: DraftField | null;
}

export interface DraftProblems {
  /** Save is refused while any of these stand. */
  errors: DraftProblem[];
  /** Legal, but probably not what was meant — shown, never blocking. */
  warnings: DraftProblem[];
}

/** The sentences alone, for a summary or a test. */
export function messages(problems: DraftProblem[]): string[] {
  return problems.map((p) => p.message);
}

function label(row: DraftRow, index: number): string {
  const name = row.name.trim() || row.code.trim();
  return `Row ${index + 1}${name ? ` (${name})` : ''}`;
}

export function checkDraft(rows: DraftRow[], isProduct: boolean): DraftProblems {
  const errors: DraftProblem[] = [];
  const warnings: DraftProblem[] = [];

  if (rows.length === 0) {
    errors.push({ message: 'A version needs at least one parameter.', row: null, field: null });
    return { errors, warnings };
  }

  const codes = new Map<string, number>();
  rows.forEach((row, i) => {
    const at = label(row, i);
    const error = (field: DraftField | null, message: string) => errors.push({ message: `${at}: ${message}`, row: i, field });
    const warn = (field: DraftField | null, message: string) => warnings.push({ message: `${at}: ${message}`, row: i, field });

    const code = row.code.trim().toUpperCase();
    if (!code) error('code', 'a code is required.');
    if (!row.name.trim()) error('name', 'a name is required.');
    if (code) {
      if (codes.has(code)) error('code', `the code ${code} is already used by row ${codes.get(code)! + 1}.`);
      else codes.set(code, i);
    }

    const samples = parseNumber(row.sampleCount);
    if (samples === undefined || samples === null || !Number.isInteger(samples) || samples < 1) {
      // The samples field is drawn for a measurement only; on a yes/no row the Answer select is what is on screen.
      error(row.valueKind === 'NUMERIC' ? 'sampleCount' : 'valueKind', 'samples must be a whole number, 1 or more.');
    }

    const interval = parseNumber(row.intervalMinutes);
    if (interval === undefined || (interval !== null && (!Number.isInteger(interval) || interval < 1))) {
      // "Every" is drawn for an in-process check only; otherwise the Checked select is what is on screen.
      error(isInProcess(row.checkKind) ? 'intervalMinutes' : 'checkKind', '"every" must be a whole number of minutes, or empty.');
    } else if (isInProcess(row.checkKind) && interval === null) {
      warn('intervalMinutes', 'no interval, so the in-process check is asked once per run and never repeats.');
    }

    if (row.requiresInstrument && !row.instrumentKind.trim()) {
      error('instrumentKind', 'name the kind of instrument, or the gate has nothing to match a meter against.');
    }

    const fields: Array<[string, DraftField, number | null | undefined]> = [
      ['reject min', 'minValue', parseNumber(row.minValue)],
      ['action lower', 'actionLower', parseNumber(row.actionLower)],
      ['target', 'nominal', parseNumber(row.nominal)],
      ['action upper', 'actionUpper', parseNumber(row.actionUpper)],
      ['reject max', 'maxValue', parseNumber(row.maxValue)],
    ];
    const value = (field: DraftField) => fields.find(([, f]) => f === field)![2];
    for (const [name, field, v] of fields) {
      if (v === undefined) error(row.valueKind === 'NUMERIC' ? field : 'valueKind', `${name} is not a number.`);
    }

    // The server's own refusal, said before Save (api tools/specDraft.ts): a COFA check with no
    // reject limit and no expected text could never judge a certificate (UX-29).
    const hasText = row.expectedText.trim() !== '';
    const noCofaLimit =
      row.checkKind === 'COFA' && !hasText && parseNumber(row.minValue) === null && parseNumber(row.maxValue) === null;
    if (noCofaLimit) {
      error(
        'expectedText',
        'a certificate check needs a reject min, a reject max or the words the certificate must read. With none, no certificate could ever be judged on it.',
      );
    }

    if (row.valueKind === 'ATTRIBUTE') {
      // CK_SpecParameter_Attribute: a yes/no question has no numbers at all. The
      // limits are not drawn for a yes/no row, so the Answer select carries it.
      if (fields.some(([, , v]) => v !== null && v !== undefined)) {
        error('valueKind', 'a yes/no check has no limits or target — clear them, or make it a measured check.');
      }
      return;
    }

    const min = value('minValue') ?? null;
    const lower = value('actionLower') ?? null;
    const upper = value('actionUpper') ?? null;
    const max = value('maxValue') ?? null;
    // The same four pairs CK_SpecParameter_Bands checks, in words. Each is put
    // under the upper field of the pair: the one that is below what sits beneath it.
    const crossed: Array<[DraftField, string]> = [];
    if (min !== null && lower !== null && min > lower) crossed.push(['actionLower', 'reject min is above action lower']);
    if (lower !== null && upper !== null && lower > upper) crossed.push(['actionUpper', 'action lower is above action upper']);
    if (upper !== null && max !== null && upper > max) crossed.push(['maxValue', 'action upper is above reject max']);
    if (min !== null && max !== null && min > max) crossed.push(['maxValue', 'reject min is above reject max']);
    for (const [field, c] of crossed) error(field, `${c}. The action band sits inside the reject band.`);

    if (row.aggregation === 'AQS' && (value('nominal') === null || lower === null || min === null)) {
      // verdict.ts refuses every capture otherwise (aqs_no_nominal / aqs_no_tolerances).
      const missing: DraftField = value('nominal') === null ? 'nominal' : lower === null ? 'actionLower' : 'minValue';
      error(missing, 'AQS needs a target, an action lower (T1) and a reject min (T2).');
    }

    // ⚠ Not on a row judged by its expected text (UI/UX audit, 31 Oct 2026, UX-29): MATERIAL-15's
    // Identification ("Passes Test") was warned "every value reads as IN" when it is judged by words,
    // and a warning that is false on 18 rows teaches people to skip the true one. Nor beside the
    // COFA error above, which already says it.
    if ([min, lower, upper, max].every((v) => v === null) && !hasText && !noCofaLimit) {
      warn('maxValue', 'no limits at all, so every value reads as IN.');
    }
  });

  if (isProduct && !rows.some((r) => r.checkKind === 'FIRST_OFF' || r.checkKind === 'BOTH')) {
    warnings.push({
      message: 'No first-off check: this version could be approved, but no run could ever start against it.',
      row: null,
      field: null,
    });
  }

  return { errors, warnings };
}

/** The rows as `POST /specs` wants them. Sequence is the order on screen — on a
 *  docked iPad the order of the prompts IS the interface. */
export function toPayload(rows: DraftRow[]): Record<string, unknown>[] {
  return rows.map((row, i) => {
    const attribute = row.valueKind === 'ATTRIBUTE';
    const n = (v: string) => (attribute ? null : (parseNumber(v) ?? null));
    return {
      code: row.code.trim().toUpperCase(),
      name: row.name.trim(),
      unit: row.unit.trim() || null,
      sequence: i + 1,
      checkKind: row.checkKind,
      intervalMinutes: isInProcess(row.checkKind) ? (parseNumber(row.intervalMinutes) ?? null) : null,
      valueKind: row.valueKind,
      minValue: n(row.minValue),
      actionLower: n(row.actionLower),
      nominal: n(row.nominal),
      actionUpper: n(row.actionUpper),
      maxValue: n(row.maxValue),
      sampleCount: attribute ? 1 : (parseNumber(row.sampleCount) ?? 1),
      aggregation: attribute ? 'ALL' : row.aggregation,
      controlPointType: row.controlPointType,
      section: row.section.trim() || null,
      requiresEvidenceOnFail: attribute ? row.requiresEvidenceOnFail : false,
      requiresEvidence: row.requiresEvidence,
      requiresInstrument: row.requiresInstrument,
      instrumentKind: row.requiresInstrument ? row.instrumentKind.trim() || null : null,
      expectedText: row.expectedText.trim() || null,
      isMandatory: row.isMandatory,
      // Only a row carried over from the version names its source; a row added here has none.
      ...(row.sourceParameterId !== null ? { referencePhotosFrom: row.sourceParameterId } : {}),
    };
  });
}

/**
 * A MATERIAL version's own terms (migration 014), carried into a draft made from it. They are
 * signed with the version, and the server reads a missing cofaRequired as required and a
 * missing minimum shelf life as none: a copy that dropped them would change what the next
 * certificate is held to (the migration-006 trap). Any other subject has none to send.
 */
export function versionTerms(
  subjectType: string,
  v: { minShelfLifeDays?: number | null; cofaRequired?: boolean | null },
): { minShelfLifeDays?: number | null; cofaRequired?: boolean } {
  return subjectType === 'MATERIAL' ? { minShelfLifeDays: v.minShelfLifeDays ?? null, cofaRequired: v.cofaRequired ?? true } : {};
}
